import React, { useCallback, useEffect, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import Pagination from '@cloudscape-design/components/pagination';
import Select from '@cloudscape-design/components/select';
import Table from '@cloudscape-design/components/table';
import { useNavigate } from 'react-router-dom';
import { apiJson } from '../utils/api';

interface AirframeSummary {
  airframe_key: string;
  n_number?: string;
  nose_name?: string;
  make?: string;
  model?: string;
  story_status?: string;
  story_generated_at?: string;
  story_found?: boolean;
}

const STATUS_OPTIONS = [
  { label: 'All statuses', value: 'all' },
  { label: 'Published', value: 'approved' },
  { label: 'Pending', value: 'pending' },
  { label: 'Unpublished', value: 'rejected' },
];

const PAGE_SIZE = 25;

const AirframeStoriesPage: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<AirframeSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('all');
  const [pageCursors, setPageCursors] = useState<(string | undefined)[]>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);
  const [nextKey, setNextKey] = useState<string | undefined>(undefined);

  const fetchPage = useCallback(
    async (idx: number, cursor: string | undefined): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const qs = new URLSearchParams({ pageSize: String(PAGE_SIZE) });
        if (cursor) qs.set('nextPageKey', cursor);
        if (status !== 'all') qs.set('status', status);
        const data = await apiJson<{ items: AirframeSummary[]; nextPageKey?: string }>(
          `/admin/airframe-stories?${qs.toString()}`,
        );
        setItems(data.items);
        setNextKey(data.nextPageKey);
        setPageIndex(idx);
        if (data.nextPageKey) {
          setPageCursors((prev) => {
            const c = [...prev];
            c[idx + 1] = data.nextPageKey;
            return c;
          });
        }
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [status],
  );

  useEffect(() => {
    setPageCursors([undefined]);
    void fetchPage(0, undefined);
  }, [status, fetchPage]);

  return (
    <ContentLayout header={<Header variant="h1">Airframe Stories</Header>}>
      {error && <Alert type="error">{error}</Alert>}
      <Table
        loading={loading}
        loadingText="Loading airframe stories"
        items={items}
        variant="full-page"
        onRowClick={({ detail }) =>
          navigate(`/airframe-stories/${encodeURIComponent(detail.item.airframe_key)}`)
        }
        columnDefinitions={[
          {
            id: 'aircraft',
            header: 'Aircraft',
            cell: (s) => (
              <Link
                href={`/airframe-stories/${encodeURIComponent(s.airframe_key)}`}
                onFollow={(e) => {
                  e.preventDefault();
                  navigate(`/airframe-stories/${encodeURIComponent(s.airframe_key)}`);
                }}
              >
                {s.nose_name || [s.make, s.model].filter(Boolean).join(' ') || s.airframe_key}
              </Link>
            ),
            isRowHeader: true,
          },
          { id: 'tail', header: 'Tail #', cell: (s) => s.n_number ?? '—' },
          {
            id: 'status',
            header: 'Status',
            cell: (s) => (
              <Badge color={(s.story_status ?? 'pending') === 'approved' ? 'green' : 'grey'}>
                {s.story_status ?? 'pending'}
              </Badge>
            ),
          },
          {
            id: 'generated',
            header: 'Generated',
            cell: (s) => s.story_generated_at?.slice(0, 10) ?? '—',
          },
        ]}
        filter={
          <Select
            selectedOption={STATUS_OPTIONS.find((o) => o.value === status) ?? STATUS_OPTIONS[0]}
            onChange={(e) => setStatus(e.detail.selectedOption.value ?? 'all')}
            options={STATUS_OPTIONS}
          />
        }
        pagination={
          <Pagination
            currentPageIndex={pageIndex + 1}
            pagesCount={pageCursors.length}
            openEnd={Boolean(nextKey)}
            onChange={({ detail }) => {
              const idx = detail.currentPageIndex - 1;
              void fetchPage(idx, pageCursors[idx]);
            }}
          />
        }
        empty={
          <Box textAlign="center" color="text-body-secondary">
            No airframe stories yet. They generate for named aircraft / warbirds on identify.
          </Box>
        }
        header={
          <Header counter={nextKey ? `(${items.length}+)` : `(${items.length})`}>
            Per-airframe stories
          </Header>
        }
      />
    </ContentLayout>
  );
};

export default AirframeStoriesPage;
